import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { apiClient } from '@/api/axiosClient';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { BranchDto, WarehouseDto } from '@/types/organization';

function buildSchema(t: (key: string) => string) {
  return z.object({
    branchId: z.string().min(1),
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    address: z.string().nullable(),
  });
}

export type WarehouseFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface WarehouseFormDialogProps {
  open: boolean;
  warehouse: WarehouseDto | null;
  onClose: () => void;
  onSubmit: (values: WarehouseFormValues) => void;
  loading?: boolean;
}

export default function WarehouseFormDialog({ open, warehouse, onClose, onSubmit, loading }: WarehouseFormDialogProps) {
  const { t } = useTranslation('warehouseMaster');
  const schema = buildSchema(t);

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/branches')).data,
    enabled: open,
  });

  const { control, handleSubmit, reset } = useForm<WarehouseFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { branchId: '', code: '', name: '', address: null },
  });

  useEffect(() => {
    if (open) {
      reset(
        warehouse
          ? { branchId: warehouse.branchId, code: warehouse.code, name: warehouse.name, address: warehouse.address }
          : { branchId: '', code: '', name: '', address: null },
      );
    }
  }, [open, warehouse, branches, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{warehouse ? t('editWarehouse') : t('addWarehouse')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="branchId"
            control={control}
            render={({ field, fieldState }) => (
              <SearchableSelect
                label="Branch" value={field.value} onChange={field.onChange} fullWidth disabled={Boolean(warehouse)}
                helperText={fieldState.error?.message}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
            )}
          />
          <Controller
            name="code"
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} label={t('form.code')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
            )}
          />
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.address')} fullWidth multiline rows={2} />}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
